<script>
import i18n from '@/plugins/i18n';
import { mapActions } from 'vuex';
import Card from "../../../components/Card.vue";
import SustainfinityTable from '../../../components/SustainfinityTable.vue';
import NameBubble from '../../../components/name-bubble.vue';

export default {
    components: {
        Card,
        SustainfinityTable,
        NameBubble
    },

    data() {
        return {
            categories: [],
            facilities: [],
            
            officers: [],
            kpis: [],
            
            selectedTabIndex: 0,
            selectedCategoryId: 0,
            selectedOfficerToFilterBy: null,
            
            showOfficersBubble: null, // the variable that holds the data for which "show more" name bubble is clicked
            
            selectedMetrics: [],
            selectedOfficers: [],
            clickedAction: null,

            showDescription: true,
        }
    },

    computed: {
        notAllowed() {
            return this.$user.role_id > 4;
        },

        availableTabs() {
            if (this.facilities.length <= 1) {
                return [
                    {
                        facility_id: 0,
                        facility_name: i18n.t('metric_assignments.entire_company')
                    }
                ];
            }

            return [
                {
                    facility_id: 0,
                    facility_name: i18n.t('metric_assignments.entire_company')
                },
                ...this.facilities.map(x => {
                    return {
                        facility_id: x.facility_id,
                        facility_name: x.facility_name,
                    }
                })
            ];
        },

        headers() {
            return [
                {
                    key: 'view',
                    col: 'viewers',
                    is_button: true,
                    can_send_email: false,
                    text: i18n.t('metric_assignments.headers.view'),
                    flex: 1,
                },
                {
                    key: 'edit',
                    col: 'editors',
                    is_button: true,
                    can_send_email: true,
                    text: i18n.t('metric_assignments.headers.edit'),
                    flex: 1,
                },
                {
                    key: 'audit',
                    col: 'auditors',
                    is_button: true,
                    can_send_email: true,
                    text: i18n.t('metric_assignments.headers.audit'),
                    flex: 1,
                },
                {
                    key: 'selection',
                    col: null,
                    is_button: false,
                    can_send_email: false,
                    text: i18n.t('metric_assignments.headers.selection'),
                    flex: 1,
                },
                {
                    key: 'metrics',
                    col: null,
                    is_button: false,
                    can_send_email: false,
                    text: i18n.t('metric_assignments.headers.metrics'),
                    flex: 6,
                },
            ];
        },

        groupingHeaders() {
            return [
                {
                    key: 'actions',
                    text: i18n.t('metric_assignments.headers.assignments'),
                    flex: 3,
                },
                {
                    key: 'selection',
                    text: '',
                    flex: 1,
                },
                {
                    key: 'metrics',
                    text: '',
                    flex: 6,
                },
            ];
        },

        officersTableHeaders() {
            return [
                {
                    key: '#',
                    text: '#',
                    flex: 0.2,
                },
                {
                    key: 'firstname',
                    text: i18n.t('metric_assignments.headers.firstname'),
                    flex: 1,
                },
                {
                    key: 'surname',
                    text: i18n.t('metric_assignments.headers.surname'),
                    flex: 1,
                },
                {
                    key: 'email',
                    text: i18n.t('metric_assignments.headers.email'),
                    flex: 1,
                },
                {
                    key: 'user_department_name',
                    text: i18n.t('metric_assignments.headers.user_department_name'),
                    flex: 1,
                },
            ];
        },

        generalColorCode() {
            return this.getColor(this.selectedCategoryId);
        },

        CONTEXT_MENU_ITEMS() {
            return [
                {
                    key: 'delete_view',
                    text: i18n.t('metric_assignments.delete_from_all_view'),
                },
                {
                    key: 'delete_edit',
                    text: i18n.t('metric_assignments.delete_from_all_edit'),
                },
            ];
        },
    },

    watch: {
        async selectedTabIndex() {
            await this._getGroupedMetrics(this.selectedCategoryId);
        },
    },

    async mounted() {
        await this._getCompanyOfficers();
        await this._getCategories();
        await this._getFacilities();
    },

    methods: {
        ...mapActions('KPIsMetrics', [
            'getCategories',
        ]),

        ...mapActions('company', [
            'getFacilities',
        ]),
        
        ...mapActions('admin', [
            'getGroupedMetrics',
            'getCompanyOfficers',
            'handleMetricAssignment',
            'deleteMetricAssignment',
            'revokeAllAssignments',
        ]),

        async _getCategories() {
            await this.getCategories()
                .then(async res => {
                    await this.selectCategory(res.data[0].sust_category_id);
                    this.categories = res.data;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _getFacilities() {
            const params = {
                company_id: this.$user.company_id,
                is_deleted: false
            }

            await this.getFacilities(params)
                .then(res => {
                    this.facilities = res.data;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _getGroupedMetrics(id) {
            const params = {
                category_id: id,
                facility_id: this.availableTabs[this.selectedTabIndex].facility_id,
            }

            await this.getGroupedMetrics(params)
                .then(res => {
                    this.kpis = res.data;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },
        
        async _getCompanyOfficers() {
            await this.getCompanyOfficers()
                .then(res => {
                    this.officers = res.data;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _handleMetricAssignment() {
            const header = this.headers.find(x => x.key === this.clickedAction);
            if (!header)
                return;

            if (this.selectedOfficers.length === 0 || this.selectedMetrics.length === 0)
                return;

            const params = {
                type: header.col,
                body: {
                    user_ids: this.selectedOfficers,
                    metric_ids: this.selectedMetrics,
                    facility_id: this.availableTabs[this.selectedTabIndex].facility_id,
                    send_email: header.can_send_email ? this.sendEmail : false,
                }
            };

            await this.handleMetricAssignment(params)
                .then(async (res) => {
                    // any other way that doesn't involve sending a request to the backend is way too expensive for the frontend
                    // would much rather just send a fetch request after each action than load that weight on to a potato pc
                    await this._getGroupedMetrics(this.selectedCategoryId);
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
                .finally(async () => {
                    await this.resetAllMetrics();
                    this.selectedOfficers = [];
                    this.clickedAction = null;
                })
        },

        async _deleteMetricAssignment(user_id, metric_id, type) {
            const params = {
                user_id: user_id,
                facility_id: this.availableTabs[this.selectedTabIndex].facility_id,
                metric_id: metric_id,
                type: type,
            };

            await this.deleteMetricAssignment(params)
                .then(async (res) => {
                    // any other way that doesn't involve sending a request to the backend is way too expensive for the frontend
                    // would much rather just send a fetch request after each action than load that weight on to a potato pc
                    await this._getGroupedMetrics(this.selectedCategoryId);
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _revokeAllAssignments(user_id, type) {
            const params = {
                user_id: user_id,
                facility_id: this.availableTabs[this.selectedTabIndex].facility_id,
                type: type,
            };

            await this.revokeAllAssignments(params)
                .then(async (res) => {
                    // any other way that doesn't involve sending a request to the backend is way too expensive for the frontend
                    // would much rather just send a fetch request after each action than load that weight on to a potato pc
                    await this._getGroupedMetrics(this.selectedCategoryId);
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async selectCategory(id) {
            await this._getGroupedMetrics(id);
            this.selectedCategoryId = id;
        },

        selectKpi(e, item) {
            if (!item || !item.sust_metric_kpis)
                return;

            for (let i = 0; i < item.sust_metric_kpis.length; i++) {
                this.selectMetric(e, item.sust_metric_kpis[i]);
            }
        },

        selectMetric(e, item) {
            if (!item || item.sust_metric_kpi_id === undefined)
                return;

            const checked = e.target.checked;
            const el = document.getElementById(`metric_${item.sust_metric_kpi_id}`);
            el.checked = checked;

            if (checked) {
                if (!this.selectedMetrics.some(x => x == item.sust_metric_kpi_id))
                    this.selectedMetrics.push(item.sust_metric_kpi_id);
            } else {
                this.selectedMetrics = this.selectedMetrics.filter(x => x != item.sust_metric_kpi_id);
            }
        },

        async resetAllMetrics() {
            const kpi_inputs = document.getElementsByClassName('kpi-checkbox-input');
            for (let i = 0; i < kpi_inputs.length; i++) {
                const element = kpi_inputs[i];
                element.checked = false;
            }

            for (let i = 0; i < this.selectedMetrics.length; i++) {
                const id = this.selectedMetrics[i];
                const el = document.getElementById(`metric_${id}`);
                el.checked = false;
            }

            this.selectedMetrics = [];
        },

        selectOfficer(e, item) {
            if (!item)
                return;

            const checked = e.target.checked;
            if (checked) {
                if (!this.selectedOfficers.some(x => x == item.user_id))
                    this.selectedOfficers.push(item.user_id);
            } else {
                this.selectedOfficers = this.selectedOfficers.filter(x => x != item.user_id);
            }
        },

        selectOfficerToFilterBy(_, officer) {
            if (this.selectedOfficerToFilterBy != null && this.selectedOfficerToFilterBy.user_id === officer.user_id) {
                this.selectedOfficerToFilterBy = null;
            } else {
                this.selectedOfficerToFilterBy = officer;
            }
        },
        
        resetSelectedOfficers() {
            this.selectedOfficers = [];
            this.sendEmail = false;
        },

        selectTab(idx) {
            this.selectedTabIndex = idx;
        },

        handleClickedAction(action) {
            this.clickedAction = action;
            if (action != null) {
                this.$refs.officersPopup.openPopup();
            }
        },

        async handleContextMenuAction(key, data) {
            if (key === 'delete_edit') {
                await this._revokeAllAssignments(data.user_id, 'editors');
            } else if (key === 'delete_view') {
                await this._revokeAllAssignments(data.user_id, 'viewers');
            } else {
                console.error('Invalid context menu action: ', key);
            }
        },

        async handleBubbleClick(_, assignee, metric, header, clickX) {
            if (clickX) {
                await this.handleXClick(assignee, metric, header);
            } else {
                await this.handleShowAllAssignees(metric, header);
            }
        },

        async handleXClick(assignee, metric, header) {
            await this._deleteMetricAssignment(assignee.id, metric.sust_metric_kpi_id, header.col);
        },
        
        async handleShowAllAssignees(metric, header) {
            if (this.showOfficersBubble == null || this.showOfficersBubble.key != header.key || this.showOfficersBubble.sust_metric_kpi_id != metric.sust_metric_kpi_id) {
                this.showOfficersBubble = {
                    key: header.key, 
                    sust_metric_kpi_id: metric.sust_metric_kpi_id
                };
            } else {
                this.showOfficersBubble = null;
            }
        },

        getAssignees(item, key) {
            let col = null;
            if (key === 'view') {
                col = 'viewers';
            } else if (key === 'edit') {
                col = 'editors';
            } else if (key === 'audit') {
                col = 'auditors';
            } else {
                return null;
            }

            if (!item)
                return null;

            if (item.sust_metric_kpi_id === null && item.sust_kpi_id != null && item.metrics != null) {
                return null;
            } else if (item.sust_metric_kpi_id != null && item[col] != null) {
                if (this.selectedOfficerToFilterBy != null) {
                    return item[col].filter(x => x.id === this.selectedOfficerToFilterBy.user_id);
                } else {
                    return item[col];
                }
            } else {
                return null;
            }
        },

        getAssigneeCount(item, key) {
            let col = null;
            if (key === 'view') {
                col = 'viewers';
            } else if (key === 'edit') {
                col = 'editors';
            } else if (key === 'audit') {
                col = 'auditors';
            } else {
                return null;
            }

            if (!item)
                return null;

            if (item.sust_metric_kpi_id === null && item.sust_kpi_id != null && item.metrics != null) {
                return null;
            } else if (item.sust_metric_kpi_id != null && item[col] != null) {
                return item[col].length;
            } else {
                return null;
            }
        },

        checkShowAllOfficerBubble(header, metric) {
            return (this.showOfficersBubble != null && 
                this.showOfficersBubble.key === header.key && 
                this.showOfficersBubble.sust_metric_kpi_id === metric.sust_metric_kpi_id);
        },

        getColor(id) {
            if (id == 1) {
                return '#fa8a14'
            } else if (id == 2) {
                return '#44a900'
            } else if (id == 3) {
                return '#de0222'
            } else if (id == 4) {
                return '#004578'
            }
        },
    }
}
</script>

<template>
    <div class="metric-assignments-page">
        <div class="tabs-ctr" v-if="availableTabs.length > 1">
            <div v-for="(tab, idx) in availableTabs" :key="tab.facility_id"
                @click="selectTab(idx)"
                :class="{'selected-tab': selectedTabIndex === idx}"
            >
                {{ tab.facility_name }}
            </div>
        </div>

        <div v-if="!notAllowed" class="officer-filters-ctr">
            <name-bubble 
                v-for="(officer, idx) in officers" :key="officer.user_id"
                :Words="[officer.firstname, officer.surname]"
                :XonHover="false"
                :Radius="52"
                :PutEmphasis="selectedOfficerToFilterBy == null ? true : (officer.user_id == selectedOfficerToFilterBy.user_id)"
                :style="{
                    transform: `translateX(-${idx*10}%)`,
                }"
                @clickBubble="selectOfficerToFilterBy($event, officer)"
                v-context-menu="{
                    items: CONTEXT_MENU_ITEMS, 
                    onItemClick: handleContextMenuAction, 
                    data: officer
                }"
                v-sust-tooltip="{text: `${officer.firstname} ${officer.surname}`, bgColor: 'var(--sustainfinity-navy)'}"
            />
        </div>

        <div v-if="showDescription" class="description-bar" @click="() => showDescription = false">
            <div>{{ $t('metric_assignments.description_1') }}</div><br>
            <div>{{ $t('metric_assignments.description_2') }}</div><br>
            <div>{{ $t('metric_assignments.description_3') }}</div>
        </div>

        <div class="categories-tabs-container" :style="{'border-bottom': `thick solid ${generalColorCode}`}">
            <div v-for="(category, index) in categories" :key="category.sust_category_id"
                @click="selectCategory(category.sust_category_id)" 
                class="category flex-all-centered-col"
                :class="{ 'selected-category': selectedCategoryId === category.sust_category_id }" 
                :style="{
                    backgroundColor: selectedCategoryId === category.sust_category_id ? getColor(category.sust_category_id) : 'whitesmoke',
                    color: getColor(category.sust_category_id),
                    zIndex: selectedCategoryId === category.sust_category_id ? 5 : 5 - category.sust_category_id,
                    transform: `translateX(${index * 85}%)`
                }"
            >
                {{ category.sust_category_name }}
            </div>
        </div>

        <Card v-if="!notAllowed" 
            width="100" 
            style="margin-top: 0 !important;"
        >
            <div class="items-container">
                <div class="item-row">
                    <div v-for="header in groupingHeaders" :key="header.key"
                        class="row-headers" :style="{flex: header.flex}" style="font-weight: bold;"
                    >
                        <div style="width: fit-content;">
                            {{ header.text }}
                        </div>
                    </div>
                </div>

                <div class="item-row">
                    <div v-for="header in headers" :key="header.key"
                        class="row-headers" :style="{flex: header.flex}" style="font-weight: bold;"
                    >
                        <div style="width: fit-content;">
                            {{ header.text }}
                        </div>

                        <button v-if="header.is_button"
                            class="icon-button"
                            @click="handleClickedAction(header.key)"
                        >
                            <i class="fa-solid fa-circle-plus" :style="{color: generalColorCode}"></i>
                        </button>
                    </div>
                </div>

                <div v-for="kpi in kpis" :key="kpi.sust_kpi_id"
                    class="item-row" style="border-color: rgb(225, 225, 225);"
                >
                    <div v-for="header in headers" :key="header.key"
                        class="row-headers" :style="{flex: header.flex}"
                    >
                        <div v-if="header.is_button">
                            -
                        </div>
                        <div v-else-if="header.key === 'selection'">
                            <input class="sust-checkbox kpi-checkbox-input" type="checkbox" @change="selectKpi($event, kpi)" :style="{'border-color': `${generalColorCode}`, 'border-width': '2px'}">
                        </div>
                        <div v-else-if="header.key === 'metrics'" class="kpi-item-special" :style="{color: generalColorCode}">
                            {{ kpi.sust_kpi_name }}
                        </div>
                    </div>

                    <div v-if="kpi.sust_metric_kpis" class="items-container">
                        <div v-for="metric in kpi.sust_metric_kpis" :key="metric.sust_metric_kpi_id"
                            class="item-row" style="border-bottom: 0;"
                        >
                            <div v-for="header in headers" :key="header.key"
                                :style="{flex: header.flex}"
                            >
                                <div v-if="(header.is_button) && getAssignees(metric, header.key) === null">
                                    -
                                </div>
                                <div v-if="(header.is_button) && getAssignees(metric, header.key) != null"
                                    class="flex-all-centered-row" style="position: relative; width: fit-content !important;"
                                    :class="{'show-all-officers-bubble': checkShowAllOfficerBubble(header, metric)}"
                                >
                                    <name-bubble 
                                        v-for="(assignee, idx) in getAssignees(metric, header.key).filter((_, i) => checkShowAllOfficerBubble(header, metric) ? true : (i <= 2))" 
                                        :key="`header=${header.key}&metric=${metric.sust_metric_kpi_id}&assignee=${assignee.id}&showAll=${checkShowAllOfficerBubble(header, metric)}`"
                                        :Words="[assignee.name, assignee.surname]"
                                        :XonHover="checkShowAllOfficerBubble(header, metric) ? true : idx <= 1"
                                        :Count="checkShowAllOfficerBubble(header, metric) ? null : (idx > 1 ? getAssigneeCount(metric, header.key) - 2 : null)"
                                        :style="{
                                            transform: `translateX(-${idx*10}%)`
                                        }"
                                        @clickBubble="handleBubbleClick($event, assignee, metric, header, checkShowAllOfficerBubble(header, metric))"
                                        v-sust-tooltip="{
                                            text: `${assignee.name} ${assignee.surname}`, 
                                            bgColor: 'var(--sustainfinity-navy)',
                                            condition: !checkShowAllOfficerBubble(header, metric) ? idx <= 1 : true
                                        }"
                                    />
                                </div>
                                <div v-else-if="header.key === 'selection'">
                                    <input class="sust-checkbox" type="checkbox" @change="selectMetric($event, metric)" :id="`metric_${metric.sust_metric_kpi_id}`">
                                </div>
                                <div v-else-if="header.key === 'metrics'" class="metric-item-special">
                                    {{ metric.sust_metric_kpi_name }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>

        <div v-else>
            {{ $t('metric_assignments.directors_only') }}
        </div>

        <sust-popup ref="officersPopup" :footer="true" :header="false" size="md"
            @onHide="resetSelectedOfficers()" @ok="_handleMetricAssignment()"
        >
            <div v-if="clickedAction === 'edit' || clickedAction === 'audit'"
                class="flex-all-centered-row"
                style="color: var(--sustainfinity-navy); font-size: .8vw; font-weight: bold; justify-content: flex-start;"
            >
                <input id="send-email" type="checkbox" v-model="sendEmail" style="margin-right: .5em;">
                <label for="send-email"
                    style="margin-bottom: 0;"
                >
                    {{ $t('metric_assignments.send_email') }}
                </label>
            </div>
            <div class="officers-popup-wrapper">    
                <div class="officers-popup-row">
                    <div v-for="header in officersTableHeaders" :key="header.key"
                        :style="{flex: header.flex}"
                    >
                        {{ header.text }}
                    </div>
                </div>
                <div v-for="item in officers" :key="item.user_id" class="officers-popup-row">
                    <div v-for="header in officersTableHeaders" :key="header.key"
                        :style="{flex: header.flex}"
                    >
                        <span v-if="header.key === '#'">
                            <input type="checkbox" @change="selectOfficer($event, item)" :id="`officer_${item.user_id}`">
                        </span>
                        <span v-else>{{ item[header.key] }}</span>
                    </div>
                </div>
            </div>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
.metric-assignments-page {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    > * {
        margin-top: 1em;

        &:first-of-type {
            margin-top: 0;
        }
    }
}

.categories-tabs-container {
    height: 40px;
    width: 90%;
    position: relative;

    >.category {
        position: absolute;
        bottom: 0;

        height: 40px;
        width: 20%;
        font-weight: bold;
        font-size: .6vw;
        padding: 0 1em;
        text-align: center;

        border-top-right-radius: 150px;
        border-top-left-radius: 150px;

        border: .3em solid #d4dddf;
        border-bottom: 0;

        background-color: whitesmoke;

        transition: all 250ms;
        cursor: pointer;

        &:hover {
            filter: brightness(110%);
        }
    }

    >.selected-category {
        z-index: 99 !important;
        border: 0;
        color: white !important;
    }
}

.tabs-ctr {
    min-width: 40%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;

    border-radius: 1000px;
    overflow: hidden;

    > div {
        flex: 1;
        width: 100%;
        background-color: whitesmoke;
        color: black;
        text-align: center;
        vertical-align: middle;
        padding: .5em;

        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        
        cursor: pointer;
        transition: background-color 250ms;

        border-right: thin solid lightgray;
        &:last-of-type {
            border-right: 0 !important;
        }
    }

    > .selected-tab{
        background-color: var(--sustainfinity-navy) !important;
        color: whitesmoke !important;
    } 
}

.officer-filters-ctr {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
}

.items-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;

    > .item-row {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;

        border-bottom: thin solid lightgray; 
        padding: .5em 0;

        &:last-of-type {
            border-bottom: 0;
        }

        > div {
            display: flex;
            justify-content: center;
            align-items: center;

            > div {
                width: 100%;
                text-align: center;
            }
        }
    }
}

.kpi-item-special {
    font-weight: bold;
    font-size: .75vw;
    text-align: left !important;
}

.metric-item-special {
    font-weight: bold;
    font-size: .65vw;
    text-align: left !important;
}

.officers-popup-wrapper {
    padding-top: 1em;
    font-size: .8vw;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    > .officers-popup-row {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        text-align: center;

        background-color: whitesmoke;
        color: var(--sustainfinity-navy);
        border-bottom: thin solid lightgray;
        
        transition: background-color 250ms;

        &:hover {
            background-color: rgba(0,0,0, 0.05);
        }

        &:first-of-type {
            margin-top: 1em;
            background-color: var(--sustainfinity-navy) !important;
            color: whitesmoke;
            border-top-left-radius: .5em;
            border-top-right-radius: .5em;
        }

        &:last-of-type {
            margin-bottom: 1em;
            border-bottom-left-radius: .5em;
            border-bottom-right-radius: .5em;
            border-bottom: 0 !important;
        }

        > div {
            padding: .5em .5em;
        }
    }
}

.show-all-officers-bubble {
    z-index: 999;
    flex-wrap: wrap;
    align-items: center;
    align-content: center;
}

.description-bar {
    width: 90%;
    text-align: left;
    background-color: var(--sustainfinity-blue);
    color: whitesmoke;
    font-size: .8vw;
    padding: .5em;
    border-radius: .25em;
    box-shadow: 0 .1em .3em .05em gray;

    cursor: pointer;
    transition: background-color 250ms;
    &:hover {
        background-color: var(--sustainfinity-blue--hover);
    }
}
</style>